import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { SEO } from "@/components/site/SEO";
import { RevealSection } from "@/components/site/RevealSection";
import { MatrixPortrait } from "@/components/site/MatrixPortrait";
import { CTAButton } from "@/components/site/CTAButton";
import { IsotypeLogo } from "@/components/site/IsotypeLogo";
import { StaggerLines } from "@/components/site/StaggerLines";
import { AuroraGlow } from "@/components/site/AuroraGlow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";

const FOUNDER_IMG = "/img/antonio-salas.webp";

// Official art-directed hero asset (dog + typewriter + "Malinois Writers"
// monitor) — see ABOUT_HERO_IMPLEMENTATION.txt for the exact crop/motion
// spec this component follows.
const HERO_IMG = {
  desktopLarge: "/img/malinois-about-hero-desktop-1365x1024.webp",
  desktopSmall: "/img/malinois-about-hero-desktop-800x600.webp",
  mobileLarge: "/img/malinois-about-hero-mobile-1024x1024.webp",
  mobileSmall: "/img/malinois-about-hero-mobile-640x640.webp",
};

// Wraps the given phrases (ES/EN variants both listed — whichever appears
// in the actual text gets highlighted, the rest are simply no-ops) in the
// terracotta accent color, without altering a single word of the copy.
function highlightPhrases(text, phrases) {
  const present = phrases.filter((p) => text.includes(p));
  if (present.length === 0) return text;
  const sorted = present.slice().sort((a, b) => b.length - a.length);
  const escaped = sorted.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "g");
  return text.split(re).map((part, i) =>
    present.includes(part) ? (
      <span key={i} className="text-accent font-medium">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const WHOWEARE_HIGHLIGHTS = [
  "copywriters",
  "storytelling",
  "videografía",
  "videography",
  "inmigrantes nativos digitales",
  "digital-native immigrants",
];

const TAGLINE_HIGHLIGHTS = [
  "Trained for your brand’s mission.",
  "Trained for your brand's mission.",
];

const WHYWEEXIST_HIGHLIGHTS = [
  "potenciar el valor de las marcas",
  "grow the value of brands",
  "talento y la formación necesaria",
  "talent and the training",
  "mejor calidad",
  "better",
  "amigo fiel",
  "loyal friend",
];


// About — copy is locked verbatim from the source docx (see project brief).
// Layout follows the "10K" UX direction: editorial, asymmetric, two big
// "wow" moments (hero + closing manifesto), everything else calm.

export default function About() {
  const { t, lang, path } = useLang();
  const about = t.about;
  const [whyMalinois, whoWeAre, whyWeExist] = about.sections;

  return (
    <div className="bg-paper text-ink">
      <SEO title={about.meta.title} description={about.meta.description} lang={lang} />

      <AboutHero section={whyMalinois} />
      <IdentityCard whoWeAre={whoWeAre} whyWeExist={whyWeExist} />
      <Beliefs belief={about.beliefs[0]} />
      <Founder founder={about.founder} />
      <Closing sentences={about.closingSentences} cta={about.cta} tagline={about.tagline} path={path} />
    </div>
  );
}

// 01 — HERO / POR QUÉ MALINOIS: the official art-directed asset (dog +
// typewriter + "Malinois Writers" monitor) sits opposite the copy —
// editorial, asymmetric, not a corporate stock photo. Follows
// ABOUT_HERO_IMPLEMENTATION.txt exactly: crop, srcset, and motion values.
function AboutHero({ section }) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Max ~3-4% of the image's visual height, desktop only — off on
  // mobile/tablet (≤1024px, same threshold as useIsMobile everywhere else).
  const parallaxY = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [0, 32]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ink px-6 md:px-12 pt-32 pb-20 md:pb-24" data-testid="about-hero">
      <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 items-center gap-10 md:gap-8">
        {/* Copy — 6-7 columns, asymmetric (never centered) */}
        <div className="md:col-span-6 relative z-10 order-1">
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading font-semibold italic text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-paper [text-wrap:balance]"
            data-testid="about-title"
          >
            {section.title}
          </motion.h1>

          <StaggerLines
            className="mt-9 max-w-xl"
            lines={section.paragraphs}
            lineClassName="font-body text-lg md:text-xl text-paper/75 leading-[1.65] [text-wrap:pretty]"
          />

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="mt-10 font-heading italic text-2xl md:text-3xl text-paper leading-snug max-w-lg [text-wrap:balance]"
            data-testid="about-hero-tagline"
          >
            {highlightPhrases(section.tagline, TAGLINE_HIGHLIGHTS)}
          </motion.p>
        </div>

        {/* Photo — 5-6 columns. Real brand photography, not a mascot render.
            Grayscale, edges fading into the hero's black background instead
            of a hard rectangular crop. */}
        <motion.div
          className="md:col-span-6 order-2 relative overflow-hidden aspect-square md:aspect-[4/3]"
          style={{ y: parallaxY }}
          initial={{ opacity: 0, scale: 1.025, filter: "brightness(0.94)" }}
          animate={{ opacity: 1, scale: 1, filter: "brightness(1)" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="about-hero-media"
        >
          <div className="about-hero-mask absolute inset-0">
            <picture>
              <source
                media="(max-width: 767px)"
                srcSet={`${HERO_IMG.mobileSmall} 640w, ${HERO_IMG.mobileLarge} 1024w`}
                sizes="100vw"
              />
              <img
                src={HERO_IMG.desktopLarge}
                srcSet={`${HERO_IMG.desktopSmall} 800w, ${HERO_IMG.desktopLarge} 1365w`}
                sizes="(min-width: 1200px) 58vw, (min-width: 768px) 55vw, 100vw"
                width={1365}
                height={1024}
                alt=""
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover object-[30%_40%] md:object-center grayscale contrast-110"
              />
            </picture>
          </div>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 70% at 50% 45%, transparent 45%, #14110F 100%)",
            }}
            aria-hidden="true"
          />
        </motion.div>
      </div>
    </section>
  );
}

// 02/03 — QUIÉNES SOMOS + POR QUÉ EXISTIMOS: one highlight-card, two slides.
// Same dark-glass card as before, but "Por qué existimos" now lives inside
// it as a second panel you swipe/drag to reach instead of its own section.
// Two dots at the bottom track which panel is showing (also clickable).
function IdentityCard({ whoWeAre, whyWeExist }) {
  const [, whoNow] = whoWeAre.paragraphs;
  const [[index, direction], setSlide] = useState([0, 0]);
  const justDragged = useRef(false);

  const slides = [
    { title: whoWeAre.title, body: highlightPhrases(whoNow, WHOWEARE_HIGHLIGHTS) },
    { title: whyWeExist.title, body: highlightPhrases(whyWeExist.paragraphs[0], WHYWEEXIST_HIGHLIGHTS) },
  ];

  function paginate(dir) {
    setSlide(([i]) => {
      const next = Math.max(0, Math.min(slides.length - 1, i + dir));
      return next === i ? [i, dir] : [next, dir];
    });
  }

  function goTo(i) {
    setSlide(([current]) => (i === current ? [current, direction] : [i, i > current ? 1 : -1]));
  }

  // Clicking anywhere on the card advances to the next slide (wraps back
  // to the first), same as the dots — not just drag/swipe. Drag already
  // calls paginate() on release, so a click right after a drag is
  // suppressed for a beat to avoid double-advancing.
  function advance() {
    setSlide(([i]) => [(i + 1) % slides.length, 1]);
  }

  function handleCardClick() {
    if (justDragged.current) return;
    advance();
  }

  function handleDragEnd(_, info) {
    const { offset, velocity } = info;
    if (offset.x < -60 || velocity.x < -400) paginate(1);
    else if (offset.x > 60 || velocity.x > 400) paginate(-1);
    justDragged.current = true;
    setTimeout(() => {
      justDragged.current = false;
    }, 80);
  }

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
  };

  // Card structure borrowed from a reference "highlight card" (dark glass
  // card, icon halo, gradient title, corner glow on hover). Recolored into
  // Malinois's ink/paper/accent palette and stripped of the bounce/ping/
  // rotate loops from the original — hover reveals are a calm fade/lift,
  // nothing spins or bounces, per the brand's no-bounce rule.
  return (
    <RevealSection className="bg-paper py-24 md:py-36" as="section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 flex justify-center" data-testid="about-identity-block">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          layout
          className="group relative w-full max-w-2xl rounded-2xl border border-paper/10 bg-gradient-to-br from-ink via-[#1C1712] to-ink text-paper shadow-xl overflow-hidden"
          data-testid="about-identity-card"
        >
          {/* Ambient background layer — subtle tint + one soft glow blob */}
          <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
            <div className="absolute inset-0 bg-gradient-to-tr from-paper/[0.03] to-paper/[0.06] opacity-70" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-accentSoft/10 blur-3xl opacity-50" />
          </div>

          <AnimatePresence initial={false} mode="wait" custom={direction}>
            <motion.div
              key={index}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.6}
              onDragEnd={handleDragEnd}
              onClick={handleCardClick}
              className="relative z-10 p-10 md:p-14 flex flex-col items-center text-center cursor-pointer active:cursor-grabbing touch-pan-y"
            >
              {/* Icon halo — a static ring, no ping/pulse loop */}
              <div className="relative mb-7">
                <span
                  className="absolute inset-0 rounded-full border border-accent/25 scale-100 opacity-0 group-hover:scale-125 group-hover:opacity-100 transition-all duration-500"
                  aria-hidden="true"
                />
                <div className="relative p-5 rounded-full border border-paper/15 bg-ink/60 backdrop-blur-sm transition-transform duration-500 group-hover:scale-105">
                  <IsotypeLogo className="h-8 w-8" invert />
                </div>
              </div>

              <h2 className="font-heading font-semibold italic tracking-tight leading-[1.15] text-2xl md:text-4xl [text-wrap:balance]">
                {slides[index].title}
              </h2>

              <div className="mt-7 space-y-4 max-w-xl">
                <p className="font-body text-base md:text-lg text-paper/75 leading-[1.7] [text-wrap:pretty]">
                  {slides[index].body}
                </p>
              </div>

              <span
                className="mt-8 block w-1/4 h-px bg-accent/60 rounded-full transition-all duration-500 group-hover:w-1/3"
                aria-hidden="true"
              />

              <div className="flex gap-2 mt-6" role="tablist" aria-label="Quiénes somos / Por qué existimos">
                {slides.map((s, i) => (
                  <button
                    key={s.title}
                    type="button"
                    role="tab"
                    aria-selected={i === index}
                    aria-label={s.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(i);
                    }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === index ? "w-5 bg-paper/80" : "w-1.5 bg-paper/35 hover:bg-paper/55"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Corner glows — the "highlight" in Highlight Card, only on hover */}
          <span
            className="pointer-events-none absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-accent/15 to-transparent rounded-br-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-accent/15 to-transparent rounded-tl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            aria-hidden="true"
          />
        </motion.div>
      </div>
    </RevealSection>
  );
}

// 04 — EN QUÉ CREEMOS / WHAT WE BELIEVE: one core belief statement, centered.
// The ASCII dog animation that used to sit beside it now lives on the
// Services closing block instead. The line splits into setup (muted, small)
// and payoff (huge, accent, spring-in) — the same escalation used on the
// homepage thesis beats — so the belief reads as a punch, not a caption.
function Beliefs({ belief }) {
  const isMobile = useIsMobile();
  const reveal = (props) => (isMobile ? {} : props);
  const sentences = belief.split(/(?<=\.)\s+/).filter(Boolean);
  const [setup, ...restSentences] = sentences;
  const payoff = restSentences.join(" ");

  return (
    <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-44" as="section">
      {!isMobile && <AuroraGlow tone="brand" />}
      <div
        className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center"
        data-testid="about-beliefs-block"
      >
        <div data-testid="about-belief-statement">
          <motion.p
            {...reveal({
              initial: { opacity: 0.2, y: 14 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, margin: "-15% 0px" },
              transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
            })}
            className="font-heading italic text-2xl md:text-3xl lg:text-4xl leading-[1.25] text-paper/60 [text-wrap:balance]"
          >
            {setup}
          </motion.p>
          {payoff && (
            <motion.p
              {...reveal({
                initial: { opacity: 0, scale: 0.85 },
                whileInView: { opacity: 1, scale: 1 },
                viewport: { once: true, margin: "-15% 0px" },
                transition: { duration: 0.65, ease: [0.34, 1.56, 0.64, 1], delay: 0.25 },
              })}
              className={`mt-3 font-heading font-semibold italic text-4xl md:text-6xl lg:text-7xl leading-[1.1] [text-wrap:balance] ${
                isMobile ? "text-paper" : "text-accent"
              }`}
            >
              {payoff}
            </motion.p>
          )}
        </div>
      </div>
    </RevealSection>
  );
}

// True if the device has a mouse/trackpad-style pointer that can hover —
// as opposed to `useIsMobile`'s screen-width check, this groups touch
// tablets in with mobile regardless of how wide their viewport is.
function useHasHover() {
  const [hasHover, setHasHover] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setHasHover(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return hasHover;
}

// Founder portrait — green digital-rain (Matrix) effect. Desktop/laptop
// (real hover): triggers on mouse hover, resets on mouse leave. Mobile and
// tablet (touch, no hover): it's a tap-to-reveal easter egg — no hint,
// nothing tied to scrolling — tap the photo to toggle it, tap again to
// bring back the normal photo.
function FounderPhoto({ src, alt }) {
  const hasHover = useHasHover();
  const [active, setActive] = useState(false);

  return (
    <div
      className="relative overflow-hidden border border-borderline aspect-[3/4]"
      onMouseEnter={() => hasHover && setActive(true)}
      onMouseLeave={() => hasHover && setActive(false)}
      onClick={() => !hasHover && setActive((a) => !a)}
      data-testid="founder-photo"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover grayscale contrast-125"
      />
      <MatrixPortrait
        src={src}
        active={active}
        className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
          active ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

// 05 — EL FUNDADOR / THE FOUNDER: editorial magazine spread. Sticky
// portrait, article-style paragraphs, calm movement. The middle paragraph
// (the "pattern he saw too many times" line — the thesis of why Malinois
// exists) breaks out as a pull-quote and the closing hobby aside drops to a
// quiet footnote register, so four uniform blocks of body text become three
// distinct visual beats instead of one long read.
function Founder({ founder }) {
  const [bio1, bio2, pullQuote, aside] = founder.paragraphs;

  return (
    <RevealSection className="bg-paper py-24 md:py-36" as="section">
      <div
        className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16"
        data-testid="about-founder-block"
      >
        <aside className="md:col-span-4 md:sticky md:top-32 md:self-start">
          <FounderPhoto src={FOUNDER_IMG} alt={founder.name} />
          <div className="mt-6 flex items-center gap-3">
            <span className="block w-8 h-px bg-accent" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
              {founder.title}
            </span>
          </div>
          <h3 className="mt-4 font-heading italic text-2xl md:text-3xl leading-tight text-ink" data-testid="about-founder-name">
            {founder.name}
          </h3>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-secondary">
            {founder.role}
          </p>
        </aside>

        <div className="md:col-span-7 md:col-start-6" data-testid="about-founder-bio">
          <div className="space-y-6 max-w-xl">
            {[bio1, bio2].filter(Boolean).map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
                className={
                  i === 0
                    ? "font-body text-xl md:text-2xl text-ink leading-[1.6] first-letter:font-heading first-letter:italic first-letter:text-5xl md:first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-none first-letter:text-accent"
                    : "font-body text-lg md:text-xl text-secondary leading-[1.75]"
                }
              >
                {p}
              </motion.p>
            ))}
          </div>

          {pullQuote && (
            <motion.div
              initial={{ opacity: 0, x: -14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="my-10 md:my-12 max-w-xl border-l-2 border-accent pl-6 md:pl-8"
            >
              <p className="font-heading italic text-2xl md:text-4xl leading-[1.3] text-ink [text-wrap:balance]">
                {pullQuote}
              </p>
            </motion.div>
          )}

          {aside && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-xl font-mono text-sm italic text-secondary/80 leading-relaxed"
            >
              {aside}
            </motion.p>
          )}

          <div className="mt-14 flex items-center gap-4">
            <span className="block w-16 h-px bg-ink/20" aria-hidden="true" />
            <span className="font-heading italic text-xl md:text-2xl text-ink">
              — {founder.name}
            </span>
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

// 06 — MANIFIESTO + CTA / CLOSING: the second big "wow." One paragraph,
// exact words, revealed sentence by sentence via spans — never split into
// separate paragraphs, never reworded.
function Closing({ sentences = [], cta, tagline, path }) {
  return (
    <RevealSection className="relative overflow-hidden bg-ink py-28 md:py-40" as="section" variant="scale">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 40%, #B9513B, transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center" data-testid="about-closing-block">
        <p className="font-heading italic leading-snug text-paper" data-testid="about-manifesto">
          {sentences.map((sentence, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.3 }}
              className={`inline ${i === 1 ? "text-3xl md:text-5xl font-semibold" : "text-2xl md:text-4xl"}`}
            >
              {sentence}{" "}
            </motion.span>
          ))}
        </p>
        <div className="mt-14 flex justify-center">
          <CTAButton to={path("contact")} label={cta} size="large" testId="about-cta-button" />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 flex flex-col items-center gap-4"
        >
          <IsotypeLogo className="h-8 opacity-40" invert />
          <p className="font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-paper/50" data-testid="about-tagline">
            {tagline}
          </p>
        </motion.div>
      </div>
    </RevealSection>
  );
}
